/**
 * Pixel sprites - DESIGN_SYSTEM.md §5, ported from design/pixel-sprites.js.
 *
 * Two rules from the spec drive the shape of this file:
 *
 *   FR-14  Sprites are rasterised at MODULE INIT, not in an effect, so the
 *          first paint has real sources rather than a flash of nothing.
 *   NFR-11 `render` returns a **blob** URL, not a data URL. A data URL contains
 *          `;` and `,`, which terminate a CSS `url('...')` string.
 *
 * Legend: '.' transparent · W outline · K dark · B/b blob ramp · I case
 *         P paper · S paper shade · N maroon note · T teal · G gold
 *         L periwinkle · A per-instance tint
 */

import { BLOB_SHADE, BLOB_SHADE_2, shade } from "./shade";

type Grid = readonly string[];
type Palette = Record<string, string>;

const PAL: Palette = {
  W: "#FFFFFF",
  K: "#101018",
  B: "#2A2A3E",
  b: "#20202F",
  I: "#1E1E2E",
  P: "#E8E0C8",
  S: "#C6BA98",
  N: "#8B3A3A",
  T: "#4A9B9B",
  G: "#D4A24A",
  L: "#8A7FB0",
};

// prettier-ignore
const SHEET: Grid = [
'....................',
'.....WWWWWWWWWW.....',
'....WAAAAAAAAAAW....',
'....WAAAAAAAAAAW....',
'....WPPPPPPPPPPW....',
'....WPPPPPPNNPPW....',
'....WPPPPPPNNPPW....',
'....WPPPPPPNNPPW....',
'....WPPPPPPNNPPW....',
'....WPPPNNNNNPPW....',
'....WPPNNNNNNPPW....',
'....WPPPNNNNPPPW....',
'....WPPPPPPPPPPW....',
'....WSSSSSSSSSSW....',
'...bWWWWWWWWWWWWb...',
'..BBBBBBBBBBBBBBBB..',
'.BBBBBBBBBBBBBBBBBB.',
'..BBBBBBBBBBBBBBBB..',
'....bbbbbbbbbb......',
'....................'];

// prettier-ignore
const CASSETTE: Grid = [
'....................',
'....................',
'....................',
'...WWWWWWWWWWWWWW...',
'...WAAAAAAAAAAAAW...',
'...WAPPPPPPPPPPAW...',
'...WAPKKPPPPKKPAW...',
'...WAPKKPPPPKKPAW...',
'...WAPPPPPPPPPPAW...',
'...WAAAAAAAAAAAAW...',
'...WWWWWWWWWWWWWW...',
'....................',
'..BBBBBBBBBBBBBBBB..',
'.BBBBBBBBBBBBBBBBBB.',
'..BBBBBBBBBBBBBBBB..',
'....bbbbbbbbbb......',
'....................',
'....................',
'....................',
'....................'];

// prettier-ignore
const QBLOCK: Grid = [
'....................',
'....................',
'...WWWWWWWWWWWWWW...',
'...WTTTTTTTTTTTTW...',
'...WTAAAAAAAAAATW...',
'...WTAAAPPPPAAATW...',
'...WTAAPAAAAPAATW...',
'...WTAAAAAAPAAATW...',
'...WTAAAAPPAAAATW...',
'...WTAAAAPAAAAATW...',
'...WTAAAAAAAAAATW...',
'...WTAAAAPAAAAATW...',
'...WTAAAAAAAAAATW...',
'...WTTTTTTTTTTTTW...',
'...WWWWWWWWWWWWWW...',
'..BBBBBBBBBBBBBBBB..',
'.BBBBBBBBBBBBBBBBBB.',
'..BBBBBBBBBBBBBBBB..',
'....bbbbbbbbbb......',
'....................'];

// prettier-ignore
const CRT: Grid = [
'....................',
'....................',
'...WWWWWWWWWWWWWW...',
'...WIIIIIIIIIIIIW...',
'...WILLLLLLLLLLIW...',
'...WILLNLLLLLLLIW...',
'...WILLNNLLLLLLIW...',
'...WILLNNNLLLLLIW...',
'...WILLNNLLLLLLIW...',
'...WILLNLLLLLLLIW...',
'...WILLLLLLLLLLIW...',
'...WIIIIIIIIIIIIW...',
'...WWWWWWWWWWWWWW...',
'.....W........W.....',
'....WWW......WWW....',
'..BBBBBBBBBBBBBBBB..',
'.BBBBBBBBBBBBBBBBBB.',
'..BBBBBBBBBBBBBBBB..',
'....bbbbbbbbbb......',
'....................'];

// prettier-ignore
const CRTERR: Grid = [
'....................',
'....................',
'...WWWWWWWWWWWWWW...',
'...WIIIIIIIIIIIIW...',
'...WILLLLLLLLLLIW...',
'...WILNLLLLLLNLIW...',
'...WILLNLLLLNLLIW...',
'...WILLLNLLNLLLIW...',
'...WILLLLNNLLLLIW...',
'...WILLLNLLNLLLIW...',
'...WILLNLLLLNLLIW...',
'...WILNLLLLLLNLIW...',
'...WILLLLLLLLLLIW...',
'...WIIIIIIIIIIIIW...',
'...WWWWWWWWWWWWWW...',
'..BBBBBBBBBBBBBBBB..',
'.BBBBBBBBBBBBBBBBBB.',
'..BBBBBBBBBBBBBBBB..',
'....bbbbbbbbbb......',
'....................'];

// prettier-ignore
const LOCK: Grid = [
'....................',
'.......WWWWWW.......',
'......WKKKKKKW......',
'.....WKKWWWWKKW.....',
'.....WKKW..WKKW.....',
'.....WKKW..WKKW.....',
'...WWWWWWWWWWWWWW...',
'...WGGGGGGGGGGGGW...',
'...WGGGGGGGGGGGGW...',
'...WGGGGGKKGGGGGW...',
'...WGGGGKKKKGGGGW...',
'...WGGGGKKKKGGGGW...',
'...WGGGGGKKGGGGGW...',
'...WGGGGGKKGGGGGW...',
'...WGGGGGGGGGGGGW...',
'...WWWWWWWWWWWWWW...',
'..BBBBBBBBBBBBBBBB..',
'.BBBBBBBBBBBBBBBBBB.',
'..BBBBBBBBBBBBBBBB..',
'....bbbbbbbbbb......'];

// prettier-ignore
const SADNOTE: Grid = [
'....................',
'..........NNNN......',
'..........NNNN......',
'..........NNNN......',
'..........NNNN......',
'..........NNNN......',
'........NNNNNN......',
'.......NNNNNNN......',
'...NNNNNNNNNNN......',
'...NNNNNNNNNNN......',
'...NNKKNNKKNNN......',
'...NNKKNNKKNNN......',
'...NNNNNNNNNNN......',
'...NNNNKKKKNNN......',
'...NNNNNNNNNNN......',
'....NNNNNNNNN.......',
'....................',
'..BBBBBBBBBBBBBB....',
'...bbbbbbbbbb.......',
'....................'];

// prettier-ignore
const PDFICO: Grid = [
'............',
'..WWWWWWWW..',
'..WPPPPPPW..',
'..WPKKKKPW..',
'..WPPPPPPW..',
'..WPKKKKPW..',
'..WPPPPPPW..',
'..WPKKKPPW..',
'..WPPPPPPW..',
'..WWWWWWWW..',
'............',
'............'];

// prettier-ignore
const GPICO: Grid = [
'............',
'..WWWWWWWW..',
'..WTTTTTTW..',
'..WTKTKTKW..',
'..WTTTTTTW..',
'..WTKTKTKW..',
'..WTTTTTTW..',
'..WTKTKTKW..',
'..WTTTTTTW..',
'..WWWWWWWW..',
'............',
'............'];

const cache = new Map<string, string>();

/**
 * Rasterises one grid to a blob URL. Synchronous by design - `canvas.toBlob`
 * is async, and an async sprite cannot exist before first paint.
 */
export function render(rows: Grid, over: Palette | undefined, key: string): string {
  const cached = cache.get(key);
  if (cached) return cached;

  const height = rows.length;
  const width = rows[0].length;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    // Real in jsdom, which has no canvas implementation unless one is
    // installed. Named explicitly so a failing test says what to stub rather
    // than "cannot read property fillRect of null".
    throw new Error(
      `Cannot rasterise sprite "${key}": no 2D canvas context. ` +
        `In tests, stub HTMLCanvasElement.prototype.getContext or avoid importing design/sprites.`
    );
  }

  const palette = { ...PAL, ...over };
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const ch = rows[y][x];
      if (ch === "." || !palette[ch]) continue;
      ctx.fillStyle = palette[ch];
      ctx.fillRect(x, y, 1, 1);
    }
  }

  // Blob, not data URL (NFR-11): a data URL contains ';' and ',', which end a
  // CSS url('...') string.
  const b64 = canvas.toDataURL("image/png").split(",")[1];
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([buf], { type: "image/png" }));

  cache.set(key, url);
  return url;
}

/** Paper shade inside the sheet sprite - §5's reference usage. */
const PAPER_SHADE = shade("#E8E0C8", 0.86);

/**
 * The tinted card sprite for one game.
 *
 * Not a prebuilt map: a game's tint is creator data (§12, NFR-13, D-9), so the
 * set of tints is unknown until the creator record loads. Memoised by tint, so
 * the first call rasterises and the rest are free. Call `warmSheets` as soon as
 * the creator record is in hand to keep FR-14's "before first paint" promise.
 */
export function sheetFor(tint: string): string {
  return render(
    SHEET,
    {
      A: tint,
      B: shade(tint, BLOB_SHADE),
      b: shade(tint, BLOB_SHADE_2),
      S: PAPER_SHADE,
    },
    `sheet:${tint}`
  );
}

/** Rasterise every game's sheet up front. */
export function warmSheets(tints: readonly string[]): void {
  for (const tint of tints) sheetFor(tint);
}

/**
 * The untinted sprites, built at module init (FR-14). Importing this module is
 * what rasterises them.
 */
export const art = {
  cassette: render(CASSETTE, { A: "#4A9B9B", B: "#2A2A3E", b: "#20202F" }, "cass"),
  qblock: render(QBLOCK, { A: "#2C3E5C", T: "#4A9B9B" }, "qb"),
  crt: render(CRT, undefined, "crt"),
  crtErr: render(CRTERR, undefined, "crterr"),
  lock: render(LOCK, undefined, "lock"),
  note: render(SADNOTE, undefined, "sad"),
  pdf: render(PDFICO, undefined, "pdf"),
  gp: render(GPICO, undefined, "gp"),
} as const;

/** The four card objects an asset type may choose (D-10). */
export type CardObject = "SHEET" | "CASSETTE" | "CRT" | "QBLOCK";

/** Resolves a card object plus a game tint to a sprite URL. */
export function cardSprite(object: CardObject, tint: string): string {
  switch (object) {
    case "SHEET":
      return sheetFor(tint);
    case "CASSETTE":
      return render(CASSETTE, { A: tint, B: shade(tint, BLOB_SHADE), b: shade(tint, BLOB_SHADE_2) }, `cass:${tint}`);
    case "QBLOCK":
      return render(QBLOCK, { A: tint, B: shade(tint, BLOB_SHADE), b: shade(tint, BLOB_SHADE_2) }, `qb:${tint}`);
    case "CRT":
      return render(CRT, { B: shade(tint, BLOB_SHADE), b: shade(tint, BLOB_SHADE_2) }, `crt:${tint}`);
  }
}

export { SHEET, CASSETTE, QBLOCK, CRT, CRTERR, LOCK, SADNOTE, PDFICO, GPICO, PAL };
